<template>
    <div :class="containerClasses" style="height: 100%" @click="handleParentElementClick">
        <div class="level is-mobile has-font-size-sm has-text-white custom-height system-alert-row">
            <div class="level-left system-alert-prefix">
                <div class="level-item">
                    <slot v-if="primary" name="prepend">
                        <div v-if="totalCount > 1" class="level">
                            <div class="level-item ml-2">
                                <a href="#" class="has-text-white is-underlined" @click.prevent.stop="$emit('toggle')">
                                    <e-icon v-if="showingAll" icon="compress-alt" size="is-small" class="mt-1" />
                                    <e-icon v-else icon="expand-alt" size="is-small" class="mt-1" />
                                </a>
                            </div>
                            <div v-if="!$isMobile" class="level-item has-text-weight-bold">
                                {{ totalCount }}
                                alerts
                            </div>
                        </div>
                    </slot>
                </div>
            </div>
            <div class="level-item system-alert-main">
                <div class="level is-mobile system-alert-content">
                    <slot :alert="alert">
                        <slot v-if="!$isMobile" name="icon">
                            <div class="level-item mr-2">
                                <div v-if="alert.icon" class="mt-1">
                                    <e-icon :icon="alert.icon" size="is-small" />
                                </div>
                            </div>
                        </slot>
                        <slot name="title">
                            <div class="level-item system-alert-title">
                                <div
                                    v-if="alert.title"
                                    class="has-text-weight-bold system-alert-title-text"
                                    style="font-size: 0.85rem"
                                    v-html="alert.title"
                                />
                            </div>
                        </slot>
                        <slot name="caption">
                            <div class="level-item mr-0 system-alert-caption">
                                <div
                                    v-if="alert.caption"
                                    style="line-height: 100%"
                                    class="ellipses system-alert-caption-text"
                                    v-html="alert.caption"
                                />
                            </div>
                        </slot>

                        <slot name="action">
                            <div class="level-item system-alert-action">
                                <div v-if="alert.action_path" class="ml-2">
                                    <e-icon
                                        icon="arrow-circle-right"
                                        size="is-small"
                                        class="is-clickable mr-2 mt-2"
                                        @click.stop="clicked"
                                    />
                                </div>
                            </div>
                        </slot>
                    </slot>
                </div>
            </div>
            <div class="level-right system-alert-close">
                <div class="level-item has-text-right">
                    <slot name="close">
                        <div v-if="alert.dismissable" class="has-text-right py-1 px-3 system-alert-dismiss">
                            <e-icon
                                icon="times-circle"
                                size="is-small"
                                class="is-clickable mt-1 system-alert-dismiss-icon"
                                @click.stop="$emit('dismissed', alert)"
                            />
                        </div>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SystemAlert',

    props: {
        alert: {
            type: Object,
            required: true,
            validator: value => {
                return value && typeof value === 'object' && 'id' in value && 'color' in value;
            },
        },

        primary: {
            type: Boolean,
            required: false,
            default: false,
        },

        showingAll: {
            type: Boolean,
            required: false,
            default: false,
        },

        totalCount: {
            type: Number,
            required: false,
            default: 1,
        },
    },

    emits: ['toggle', 'dismissed'],

    computed: {
        containerClasses() {
            const classes = {};

            classes[`has-background-${this.alert.color}`] = true;

            if (this.alert.action_path) {
                classes['is-clickable'] = true;
            }

            return classes;
        },
    },

    methods: {
        clicked() {
            if (this.alert.action_path) {
                window.location.href = this.alert.action_path;
            }
        },

        handleParentElementClick() {
            this.clicked();
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.full-width {
    width: 100%;
}

.has-font-size-sm {
    font-size: 0.8rem;
}

.ellipses {
    overflow: hidden;
    padding: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.custom-height {
    height: 100%;
}

.system-alert-row {
    flex-wrap: nowrap;
    min-width: 0;
    overflow: hidden;
    /* Ensure the banner and its controls sit above other fixed elements (top bar) on small screens */
    position: relative;
    z-index: 60;
    align-items: center;
    /* Keep a stable minimum height so the dismiss control can be vertically centered */
    min-height: 35px;
}

.system-alert-prefix,
.system-alert-close {
    flex: 0 0 auto;
}

.system-alert-main {
    flex: 1 1 auto !important;
    justify-content: center;
    min-width: 0;
    overflow: hidden;
}

.system-alert-content {
    flex: 1 1 auto;
    flex-wrap: nowrap;
    justify-content: center;
    min-width: 0;
    overflow: hidden;
}

.system-alert-title,
.system-alert-caption {
    flex: 0 1 auto !important;
    min-width: 0;
}

.system-alert-caption {
    flex-shrink: 1 !important;
}

.system-alert-title-text,
.system-alert-caption-text {
    display: block;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.system-alert-close {
    align-self: stretch;
}

.system-alert-dismiss {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    /* Provide a comfortable tap target and prevent the close control from shrinking too small */
    min-width: 2.75rem;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    max-height: 35px;
    box-sizing: border-box;
}

.system-alert-dismiss-icon {
    margin-top: 0 !important;
    display: block;
    line-height: 1;
    /* Ensure the icon itself doesn't shift vertically and the clickable area is stable */
    height: auto;
    width: auto;
}

@include mobile {
    .ellipses {
        max-width: 100%;
    }
}

@include tablet {
    .ellipses {
        max-width: 500px;
    }
}

@include desktop {
    .ellipses {
        max-width: 900px;
    }
}

@include widescreen {
    .ellipses {
        max-width: 950px;
    }
}

@include fullhd {
    .ellipses {
        max-width: 1150px;
    }
}
</style>
